import React, { useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import CustomButton from "../common/CustomBtn";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Box, MenuItem, Typography } from "@mui/material";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  applyTranslationResources,
  getInitialTranslationResources,
  inMemoryLangCache,
  refreshTranslationResources,
} from "../../helpers/translationService";

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation("common");
  const [openNav, setOpenNav] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [openLangMenu, setOpenLangMenu] = useState(false);
  const [language, setLanguage] = useState(i18n.language || "lt");
  const isLanguageChanging = useRef(false);

  const handleLanguageChange = async (lang: string) => {
    if (isLanguageChanging.current) return;
    try {
      isLanguageChanging.current = true;
      if (!inMemoryLangCache[lang]) {
        applyTranslationResources(
          i18n,
          lang,
          getInitialTranslationResources(lang),
        );
      }

      await i18n.changeLanguage(lang);
      setLanguage(lang);
      sessionStorage.setItem("language", lang);
      setOpenLangMenu(false);
      refreshTranslationResources(i18n, lang);
    } catch (e) {
      console.error("Failed to switch language:", e);
    } finally {
      isLanguageChanging.current = false;
    }
  };

  const handleNav = () => {
    setOpenNav(!openNav);
  };

  const handleMouseEnter = (id: number) => {
    setOpenDropdownId(id);
  };

  const handleMouseLeave = () => {
    setOpenDropdownId(null);
  };

  const handleDropdown = (id: number) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const navItems = [
    {
      id: 1,
      text: t("navPlaygrounds"),
      path: "/zaidimu-aiksteles",
      hasDropdown: false,
    },
    {
      id: 2,
      text: t("navOutdoorSports"),
      path: "/lauko-sportas",
      hasDropdown: false,
    },
    {
      id: 3,
      text: t("navSafeCoatings"),
      path: "/saugios-dangos",
      hasDropdown: false,
    },
    {
      id: 4,
      text: t("navEshop"),
      path: "/el-parduotuve",
      bg: "",
      hasDropdown: true,
      dropdownItems: [
        {
          id: 1,
          text: t("navEshopPlaygrounds"),
          path: "/el-parduotuve/zaidimu-aiksteles",
        },
        {
          id: 2,
          text: t("navEshopOutdoorSports"),
          path: "/el-parduotuve/lauko-sportas",
        },
        {
          id: 3,
          text: t("navEshopSafeCoatings"),
          path: "/el-parduotuve/saugios-dangos",
        },
        {
          id: 4,
          text: t("navEshopSpareParts"),
          path: "/el-parduotuve/atsargines-dalys",
        },
        {
          id: 5,
          text: t("navEshopSmallArchitecture"),
          path: "/el-parduotuve/mazoji-architektura",
        },
      ],
    },
    {
      id: 5,
      text: t("navGallery"),
      path: "/galerija",
      hasDropdown: false,
    },
    {
      id: 6,
      text: t("navRecents"),
      path: "/naujausi",
      hasDropdown: false,
    },
  ];

  const navigate = useNavigate();

  const scrollToFooter = () => {
    const footer = document.getElementById("contact");
    if (footer) {
      footer.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full py-2 bg-white shadow-sm z-50 transition-all duration-300 semi:px-5 lg:px-[5%]`}
    >
      <Box className="items-center justify-between flex max-w-[1186px] mx-auto lg:max-w-full">
        <div className="logo">
          <NavLink to="/">
            <img
              src="/economus-logo.png"
              alt="logo"
              className="max-w-[165px] semi:max-w-[150px] lg:max-w-[165px]"
            />
          </NavLink>
        </div>

        {/* Mobile Menu Button */}
        <div className="semi:hidden">
          <div className="flex items-center gap-2">
            <Box
              className="relative flex items-center px-3 py-1 text-black border border-gray-400 cursor-pointer"
              sx={{ zIndex: 5 }}
              onMouseEnter={() => setOpenLangMenu(true)}
              onMouseLeave={() => setOpenLangMenu(false)}
            >
              <Typography
                variant="body1"
                sx={{ fontFamily: "poppins", fontWeight: "500" }}
              >
                {language === "lt" ? "LT" : "EN"}
              </Typography>

              {openLangMenu && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    backgroundColor: "white",
                    boxShadow: 2,
                    textAlign: "center",
                  }}
                >
                  <MenuItem
                    sx={{
                      py: 1,
                      px: 2,
                      color: "#000",
                      "&:hover": {
                        backgroundColor: "#b4b4b4",
                        color: "white",
                      },
                    }}
                    onClick={() => handleLanguageChange("en")}
                  >
                    English
                  </MenuItem>
                  <MenuItem
                    sx={{
                      py: 1,
                      px: 2,
                      color: "#000",
                      "&:hover": {
                        backgroundColor: "#b4b4b4",
                        color: "white",
                      },
                    }}
                    onClick={() => handleLanguageChange("lt")}
                  >
                    Lithuanian
                  </MenuItem>
                </Box>
              )}
            </Box>
            <button onClick={handleNav} className="p-2 text-black">
              {openNav ? (
                <CloseIcon fontSize="medium" />
              ) : (
                <MenuIcon fontSize="medium" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: openNav ? 0 : "100%" }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="fixed top-0 right-0 z-50 flex flex-col w-full h-full p-5 text-white shadow-lg sm:w-3/4 bg-primary"
          >
            <button onClick={handleNav} className="self-end p-2">
              <CloseIcon fontSize="medium" />
            </button>

            <ul className="flex flex-col gap-4 mt-5">
              {navItems.map((item) => (
                <React.Fragment key={item.id}>
                  <li className="relative flex justify-between pb-4 border-b">
                    <NavLink
                      to={item.path}
                      onClick={handleNav}
                      className="text-sm font-semibold"
                    >
                      {item.text}
                    </NavLink>

                    {item.hasDropdown && (
                      <button
                        onClick={() => handleDropdown(item.id)}
                        className="ml-4 text-white"
                      >
                        <ArrowDropDownIcon fontSize="large" />
                      </button>
                    )}
                  </li>
                  {item.hasDropdown && openDropdownId === item.id && (
                    <motion.ul
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="w-full py-2 pl-4 space-y-2 text-sm bg-white text-primary"
                    >
                      {item.dropdownItems!.map((subItem) => (
                        <li key={subItem.id}>
                          <NavLink to={subItem.path} onClick={handleNav}>
                            {subItem.text}
                          </NavLink>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </React.Fragment>
              ))}
              <div className="flex justify-center w-full mt-4">
                <CustomButton
                  bg="#75B129"
                  width="100%"
                  height="54px"
                  onClick={() => {
                    scrollToFooter();
                    setOpenNav(false);
                  }}
                  icon={<ContactSupportIcon />}
                >
                  {t("navContactUs")}
                </CustomButton>
              </div>
            </ul>
          </motion.div>
        </div>

        {/* Desktop Menu */}
        <ul
          className={`semi:flex justify-between items-center md:pb-0 md:pt-0 pt-12 pb-12 whitespace-nowrap absolute hidden semi:static md:z-auto left-0 w-full gap-2 lg:gap-4 md:w-auto md:px-0 px-[5%] lg:transition-all lg:duration-300 ease-in ${
            openNav ? "top-16 opacity-100 " : "top-[-490px]"
          } md:opacity-100 opacity-0 bg-primary md:bg-transparent`}
        >
          {navItems.map((item) => (
            <li
              onMouseEnter={
                item.hasDropdown ? () => handleMouseEnter(item.id) : undefined
              }
              onMouseLeave={item.hasDropdown ? handleMouseLeave : undefined}
              key={item.id}
              className="relative w-full gap-5 px-2 font-medium text-center text-white capitalize transition-transform duration-300 transform cursor-pointer md:text-black md:flexCenter hover:text-opacity-75 md:hover:text-secondary nav-item"
            >
              <div className="flex items-center gap-1">
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `text-[14px] lg:text-[16px] ${isActive ? "text-[#F7AF0C]" : ""}`
                  }
                >
                  {item.text}
                </NavLink>
                {item.hasDropdown && (
                  <div className="hidden md:flex">
                    <ArrowDropDownIcon sx={{ color: "#bfbfbf" }} />
                  </div>
                )}
              </div>

              {item.hasDropdown && item.dropdownItems && (
                <Box
                  sx={{
                    display: "block",
                  }}
                  className={`
                  absolute left-0 z-20 w-full bg-white shadow-md top-full min-w-min transform-gpu transition-all duration-700 ease-in-out ${openDropdownId === item.id ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-3 invisible"}
                `}
                >
                  {item.dropdownItems.map((dropdownItem) => (
                    <MenuItem
                      key={dropdownItem.id}
                      sx={{
                        py: 1,
                        px: 2,
                        color: "#000",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "#b4b4b4",
                          color: "white",
                        },
                      }}
                      onClick={() => {
                        navigate(dropdownItem.path);
                        setOpenNav(false);
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "poppins" }}
                      >
                        {dropdownItem.text}
                      </Typography>
                    </MenuItem>
                  ))}
                </Box>
              )}
            </li>
          ))}

          <div className="flex justify-center w-full mt-1 semi:hidden">
            <div className="mt-4">
              <CustomButton
                bg="primary.light"
                onClick={scrollToFooter}
                icon={<ContactSupportIcon />}
              >
                {t("navContactUs")}
              </CustomButton>
            </div>
          </div>
        </ul>

        {/* Language Switcher (Desktop) */}

        <div className="hidden gap-4 xl:gap-6 semi:flex">
          <Box
            className="relative flex items-center px-3 py-1 text-black border border-gray-400 cursor-pointer"
            onMouseEnter={() => setOpenLangMenu(true)}
            onMouseLeave={() => setOpenLangMenu(false)}
          >
            <Typography
              variant="body1"
              sx={{ fontFamily: "poppins", fontWeight: "600" }}
            >
              {language === "lt" ? "LT" : "EN"}
            </Typography>

            {openLangMenu && (
              <Box
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  backgroundColor: "white",
                  boxShadow: 2,
                  textAlign: "center",
                }}
              >
                <MenuItem
                  sx={{
                    py: 1,
                    px: 2,
                    color: "#000",
                    "&:hover": {
                      backgroundColor: "#b4b4b4",
                      color: "white",
                    },
                  }}
                  onClick={() => handleLanguageChange("en")}
                >
                  English
                </MenuItem>
                <MenuItem
                  sx={{
                    py: 1,
                    px: 2,
                    color: "#000",
                    "&:hover": {
                      backgroundColor: "#b4b4b4",
                      color: "white",
                    },
                  }}
                  onClick={() => handleLanguageChange("lt")}
                >
                  Lithuanian
                </MenuItem>
              </Box>
            )}
          </Box>

          <CustomButton
            onClick={scrollToFooter}
            icon={<ContactSupportIcon sx={{ fontSize: "20px" }} />}
          >
            {t("navContactUs")}
          </CustomButton>
        </div>
      </Box>
    </nav>
  );
};

export default Navbar;
